import { useState, useEffect } from "react";
import {
  createKnowledgebase,
  deleteKnowledgebase,
  getKnowledgebases,
} from "./service";
import { useOutletContext, useNavigate, useLocation } from "react-router-dom";
import { KnowledgebaseTable } from "./KnowledgebaseTable";
import { KnowledgebaseCreate } from "./KnowledgebaseCreate";
import { Heading } from "V2.0/common/heading";
import { knowledgeBasesMock } from "./mock/mock";
import { KnowledgeBaseDelete } from "./KnowledgeBaseDelete";
import { message } from "antd";
import { FolderIcon } from "@heroicons/react/24/outline";
import { showErrorToast } from "V2.0/utils";

function useKnowledgeBases(projectId) {
  const [knowledgeBases, setKnowledgeBases] = useState({
    data: [],
    isLoading: true,
  });

  const fetchKnowledgeBases = async () => {
    try {
      const response = await getKnowledgebases({ projectId });
      if (response.status === 200) {
        setKnowledgeBases({
          data: response.data.knowledgebases,
          isLoading: false,
        });
      }
    } catch (e) {
      showErrorToast(e, "Failed to get knowledge bases");
      setKnowledgeBases((prevState) => {
        return {
          ...prevState,
          isLoading: false,
        };
      });
    }
  };

  return {
    knowledgeBases,
    fetchKnowledgeBases,
  };
}

export function KnowledgeBases() {
  const { projectId, isViewOnly } = useOutletContext<{ projectId: string; isViewOnly: boolean }>();

  const { knowledgeBases, fetchKnowledgeBases } = useKnowledgeBases(projectId);

  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState({
    isOpen: false,
    item: null,
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (projectId) {
      fetchKnowledgeBases();
    }
  }, [projectId]);

  const handleCreateKnowledgebase = async (payload) => {
    message.loading("Creating Knowledge base");
    try {
      setLoading(true);
      const response = await createKnowledgebase({ projectId, payload });
      if (response.status === 201) {
        message.destroy();
        message.success("Knowledge base created");
        setLoading(false);
        const { _id } = response.data;
        let kbId = _id;
        if (kbId) {
          navigate({pathname: `/knowledgebases/${kbId}`, search: location.search}, {
            state: { knowledgebase: { _id: kbId } },
          });
        }
      }
    } catch (e) {
      message.destroy();
      showErrorToast(e, "Failed to create knowledge base");
      setLoading(false);
    }
  };

  const handleKnowledgeBaseDelete = async (_id, isForceDelete = false) => {
    message.loading("Deleting Knowledge base");
    setLoading(true);
    try {
      const response = await deleteKnowledgebase({
        projectId,
        _id,
        isForceDelete,
      });
      if (response.status === 200) {
        setLoading(false);
        message.destroy();
        message.success("Knowledge Bases deleted Successfully");
        fetchKnowledgeBases();
        setOpen({ isOpen: false, item: null });
      }
    } catch (e) {
      setLoading(false);
      message.destroy();
      showErrorToast(e, "Failed to delete knowledge base");
    }
  };

  const isKnowledgeBasesEmpty =
    !knowledgeBases.isLoading && knowledgeBases.data.length === 0;

  return (
    <>
      <div className="flex h-full max-w-6xl mx-auto py-4">
        <div className="w-full h-full flex flex-col gap-y-8">
          <div className="flex justify-between p-4 items-center">
            <Heading>Knowledge Base</Heading>
            {!isViewOnly && <KnowledgebaseCreate
              projectId={projectId}
              onCreateKnowledgebase={handleCreateKnowledgebase}
              loading={loading}
            />}
          </div>
          <div>
            {isKnowledgeBasesEmpty ? (
              <div className="h-96 w-full border border-gray-200 rounded-lg">
                <div className="h-full w-full flex justify-center items-center">
                  <div className="flex flex-col gap-y-4 items-center">
                    <FolderIcon className="size-10 text-gray-500" />
                    <div className="flex flex-col items-center gap-y-2 text-center">
                      <p className="text-base font-semibold text-gray-900">
                        Knowledge Base
                      </p>
                      <div className="w-80 text-sm text-gray-700">
                        Knowledge Bases contain information, example
                        conversations for your assistant's reference to respond
                        effectively and intelligently to queries.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-[80dvh] overflow-scroll p-2">
                <KnowledgebaseTable
                  knowledgeBases={knowledgeBases}
                  onDelete={(item) => setOpen({ isOpen: true, item })}
                  isViewOnly={isViewOnly}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {open.isOpen && (
        <KnowledgeBaseDelete
          workspaceId={projectId}
          loading={loading}
          open={open.isOpen}
          item={open.item}
          onClose={() => setOpen({ isOpen: false, item: null })}
          onDelete={handleKnowledgeBaseDelete}
        />
      )}
    </>
  );
}
